import React, { ReactNode } from "react";
import { styled } from "@mui/system";
import {
  Accordion as MuiAccordion,
  AccordionDetails as MuiAccordionDetails,
  AccordionProps,
  AccordionSummary as MuiAccordionSummary,
  AccordionSummaryProps,
  Box,
  Grid,
  Typography
} from "@mui/material";
import { Iconify } from "../../../fe-ui/atoms/Iconify";

interface Props {
  body?: string;
  children?: ReactNode;
  items?: Record<string, any>[] | null;
  title?: string | null;
}

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
  },
  '&::before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    boxShadow: 'none',
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    {...props}
  />
))(({ theme }) => ({
  padding: 0,
  flexDirection: 'row',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(180deg)',
  },
  '& .MuiAccordionSummary-content': {
    marginBlockStart: theme.spacing(2),
    marginBlockEnd: theme.spacing(2),
    marginInlineStart: 0,
    marginInlineEnd: theme.spacing(2),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: 0,
  paddingBlockEnd: theme.spacing(3),
  paddingInlineEnd: theme.spacing(5),
}));

export const LandingFaq = ({
  body,
  children,
  items,
  title,
}: Props) => {
  const [expanded, setExpanded] = React.useState<number | false>(items?.[0]?.id);

  const handleChange =
    (panel: number) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false);
    };

  const accordion = items?.map((item) => (
    <Accordion key={item?.id} expanded={expanded === item?.id} onChange={handleChange(item?.id)}>
      <AccordionSummary
        expandIcon={expanded === item?.id ?
          <Iconify width={24} icon="mingcute:minimize-line" />
          : <Iconify width={24} icon="mingcute:add-line" />}
        aria-controls={`panel${item?.id}-content`}
        id={`panel${item?.id}-header`}
      >
        <Typography variant="h3">{item?.title}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="bodyLg">{item?.text}</Typography>
      </AccordionDetails>
    </Accordion>
  ))

  const titleOrBody = title || body ? (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        {title ? <Typography variant="h2" sx={{ mb: 3 }}>{title}</Typography> : null}
        {body ? <Typography
          component="p"
          variant="bodyLg"
        >{body}</Typography> : null}
        {children}
      </Grid>
      <Grid item xs={12} md={6}>
        {accordion}
      </Grid>
    </Grid>
  ) : null;

  return (
    <Box py={5}>
      {titleOrBody ? titleOrBody : accordion}
    </Box>
  )
};
